import { ImpactFundPricingModel, PublicationStatus } from '@prisma/client';
import { z } from 'zod';
import {
  impactShopCurrencyPricesSchema,
  impactShopIdSchema,
  positiveDecimalSchema,
} from '../impact-products/impact-product-schemas';
export {
  impactShopCurrencyPricesSchema,
  impactShopIdSchema,
  impactShopSlugSchema,
} from '../impact-products/impact-product-schemas';
export type {
  ImpactShopReadiness,
  ImpactShopReadinessIssue,
  ImpactShopReadinessIssueCode,
} from '../impact-products/impact-product-schemas';
import { ImpactShopListingType } from './impact-shop-listing-types';

export const impactShopPublicationStatusSchema = z.nativeEnum(PublicationStatus);
export const impactShopExpectedUpdatedAtSchema = z.coerce.date();

const positiveMinorUnitSchema = impactShopIdSchema;

export const projectUnitPricesSchema = impactShopCurrencyPricesSchema;

export const impactShopMinimumContributionsSchema = z
  .object({
    GBP: positiveMinorUnitSchema,
    USD: positiveMinorUnitSchema,
    EUR: positiveMinorUnitSchema,
    CAD: positiveMinorUnitSchema,
    AUD: positiveMinorUnitSchema,
  })
  .strict();

const fixedPriceQuantityCommercialsObjectSchema = z
  .object({
    pricingModel: z.literal(ImpactFundPricingModel.FIXED_UNIT_PRICE),
    minQuantity: positiveDecimalSchema,
    maxQuantity: positiveDecimalSchema,
  })
  .strict();

const validateQuantityRange = <T extends { minQuantity: number; maxQuantity: number }>(commercials: T) =>
  commercials.minQuantity <= commercials.maxQuantity;

export const fixedPriceCommercialsSchema = fixedPriceQuantityCommercialsObjectSchema
  .extend({ unitPrices: impactShopCurrencyPricesSchema })
  .refine(validateQuantityRange, {
    path: ['maxQuantity'],
    message: 'Maximum quantity must be greater than or equal to minimum quantity',
  });

export const fixedPriceQuantityCommercialsSchema = fixedPriceQuantityCommercialsObjectSchema.refine(
  validateQuantityRange,
  {
    path: ['maxQuantity'],
    message: 'Maximum quantity must be greater than or equal to minimum quantity',
  },
);

export const variableContributionCommercialsSchema = z
  .object({
    pricingModel: z.literal(ImpactFundPricingModel.VARIABLE_CONTRIBUTION),
    minimumContributions: impactShopMinimumContributionsSchema,
  })
  .strict();

export const impactShopCommercialsSchema = z.union([
  fixedPriceQuantityCommercialsSchema,
  variableContributionCommercialsSchema,
]);

export const projectListingTargetSchema = z
  .object({
    targetType: z.literal(ImpactShopListingType.PROJECT),
    projectId: impactShopIdSchema,
  })
  .strict();

export const impactFundListingTargetSchema = z
  .object({
    targetType: z.literal(ImpactShopListingType.IMPACT_FUND),
    impactFundId: impactShopIdSchema,
  })
  .strict();

export const impactShopListingTargetSchema = z.discriminatedUnion('targetType', [
  projectListingTargetSchema,
  impactFundListingTargetSchema,
]);
